import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  selectCrossClientFetchedClientIds,
  selectCrossClientFilteredResults,
  setCrossClientClientsLoading,
} from '../../../../store/crossClient/crossClientResults'
import { useEffect, useMemo, useState } from 'react'
import { CrossClientQueryResultDisplay } from '../../../../models/CrossClient'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../../store/user/user'

export interface CrossClientQueryResultDetails {
  selectedResult: CrossClientQueryResultDisplay | null
  hasNextResult: boolean
  hasPreviousResult: boolean
  handleShowResultDetails: (result: CrossClientQueryResultDisplay) => void
  handleClearSelectedResult: () => void
  handleNextResult: () => void
  handlePreviousResult: () => void
}

const CLIENT_BATCH_TO_PRELOAD = 20

export const useCrossClientQueryResultDetails = (): CrossClientQueryResultDetails => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const results = useAppSelector(selectCrossClientFilteredResults)
  const fetchedClientIds = useAppSelector(selectCrossClientFetchedClientIds)

  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  const [selectedResult, setSelectedResult] = useState<CrossClientQueryResultDisplay | null>(null)

  const selectedResultIndex = useMemo(() => {
    if (!selectedResult) {
      return null
    }
    const index = results.findIndex((result) => result.id === selectedResult.id)
    return index !== -1 ? index : null
  }, [selectedResult, results])

  useEffect(() => {
    if (selectedResultIndex === null) {
      return
    }

    const nextResultIndex = selectedResultIndex + 1
    const nextResult = results[nextResultIndex]
    if (!nextResult || fetchedClientIds[nextResult.clientId]) {
      return
    }

    const nextBatch = results.slice(nextResultIndex, nextResultIndex + CLIENT_BATCH_TO_PRELOAD)
    const clientIds = nextBatch.map((result) => result.clientId).filter((clientId) => !fetchedClientIds[clientId])
    if (!clientIds.length) {
      return
    }

    const fetchMoreClients = async (clientIds: string[]) => {
      dispatch(setCrossClientClientsLoading(true))
      dispatch(addCrossClientFetchedClientIds(clientIds))
      try {
        const contacts = await triggerGetAllUsers({ accountId, ids: clientIds }).unwrap()
        if (contacts?.data) {
          const clients = contacts.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
          dispatch(addCrossClientClients(clients))
        }
      } finally {
        dispatch(setCrossClientClientsLoading(false))
      }
    }

    void fetchMoreClients(clientIds)
  }, [dispatch, accountId, selectedResultIndex, results, fetchedClientIds, triggerGetAllUsers])

  const hasNextResult = selectedResultIndex !== null && selectedResultIndex < results.length - 1
  const hasPreviousResult = selectedResultIndex !== null && selectedResultIndex > 0

  const handleShowResultDetails = (result: CrossClientQueryResultDisplay) => {
    setSelectedResult(result)
  }

  const handleClearSelectedResult = () => {
    setSelectedResult(null)
  }

  const handleNextResult = () => {
    if (hasNextResult) {
      setSelectedResult(results[selectedResultIndex + 1])
    }
  }

  const handlePreviousResult = () => {
    if (hasPreviousResult) {
      setSelectedResult(results[selectedResultIndex - 1])
    }
  }

  return {
    selectedResult,
    hasNextResult,
    hasPreviousResult,
    handleShowResultDetails,
    handleClearSelectedResult,
    handleNextResult,
    handlePreviousResult,
  }
}
